import React, { useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Link } from '@compat/router';
import useWindowSize from '@customHooks/useWindowSize';
import { Button, Picture } from '@ui';
import { crossGreyDark } from '@images';
import { IsPrerender, CheckIsBot } from '@utils/check';
import { useStores } from '@root/src/stores';
import { setCookieWarningVisible } from './cookieWarningVisibility';
import './index.scss';

const YadexMetricCounterList: {
  id: number;
  params?: Record<string, any>;
}[] = [
  {
    id: 5736784,
    params: {
      webvisor: true
    }
  },
  { id: 91535339 },
  { id: 91567655 }
];

const CookieWarning: React.FC = observer(() => {
  const isFiredFirstPageHit = useRef<boolean>(false);

  // Режим, в котором метрика уже инициализирована (null — ещё не запускали).
  // Нужен, чтобы не пересоздавать счётчики, когда режим не меняется:
  // конфиг приходит асинхронно и эффект ниже срабатывает повторно.
  const metricaFullAccessRef = useRef<boolean | null>(null);

  const { userStore, configStore } = useStores();
  // Флаг с бэка: '1' — включать метрику со всеми разрешениями,
  // не дожидаясь согласия на cookie. В сторе уже boolean.
  const forceMetrika = configStore?.getValue('force_yandex_metrika') === true;
  const [isVisible, setIsVisible] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const { isDesktop } = useWindowSize();

  const initMetrica = (fullAccess: boolean = false) => {
    if (metricaFullAccessRef.current === fullAccess) {
      return;
    }
    metricaFullAccessRef.current = fullAccess;

    YadexMetricCounterList.map(counter => {
      const params = fullAccess
        ? {
            triggerEvent: true,
            defer: false,
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            ecommerce: 'dataLayer',
            ...counter.params
          }
        : {
            triggerEvent: true,
            defer: true, // ВАЖНО: Запрещает запись файлов cookie
            clickmap: false,
            trackLinks: true,
            accurateTrackBounce: true,
            webvisor: false // Отключает запись экрана
          };

      const anyWindow = window as any;

      if (typeof anyWindow.ym !== 'undefined') {
        const counterExists = `yaCounter${counter.id}` as string;

        if (typeof anyWindow[counterExists] !== 'undefined') {
          try {
            anyWindow[counterExists].destruct();
          } catch (e) {
            console.error(`Error destructing counter ${counter.id}:`, e);
          }
        }
        anyWindow.ym(counter.id, 'init', params);
      }

      return null;
    });

    // if (!isFiredFirstPageHit.current) {
    //   isFiredFirstPageHit.current = true;

    //   try {
    //     if (typeof window.yaCounter5736784 !== 'undefined') {
    //       const pathname = location.pathname;
    //       window.yaCounter5736784.hit(pathname);
    //     }
    //   } catch (e) {
    //     console.error('Error firing first page hit:', e);
    //   }
    // }
  };

  const onFireInitEvent = useCallback(() => {
    if (!isFiredFirstPageHit.current) {
      isFiredFirstPageHit.current = true;

      try {
        if (typeof window.yaCounter5736784 !== 'undefined') {
          const pathname = location.pathname;
          window.yaCounter5736784.hit(pathname);
        }
      } catch (e) {
        console.error('Error firing first page hit:', e);
      }
    }
  }, []);

  useEffect(() => {
    const EventName = 'yacounter5736784inited';

    window?.document?.addEventListener(EventName, onFireInitEvent);

    return () => {
      window?.document?.removeEventListener(EventName, onFireInitEvent);
    };
  }, [onFireInitEvent]);

  useEffect(() => {
    if (IsPrerender()) {
      return;
    }

    const enableByInteraction = (): void => {
      setHasUserInteracted(true);
    };

    window.addEventListener('click', enableByInteraction, { once: true });
    window.addEventListener('keydown', enableByInteraction, { once: true });
    window.addEventListener('scroll', enableByInteraction, { once: true });

    return () => {
      window.removeEventListener('click', enableByInteraction);
      window.removeEventListener('keydown', enableByInteraction);
      window.removeEventListener('scroll', enableByInteraction);
    };
  }, []);

  useEffect(() => {
    if (!hasUserInteracted) {
      return;
    }

    if (CheckIsBot()) {
      setIsVisible(false);
      return;
    }

    const cookieWarningShown = localStorage.getItem('cookieWarningShown');
    const isUserLoggedIn = userStore?.isUserLoggedIn;
    const isUserLoading = userStore?.isUserLoading;

    if (cookieWarningShown) {
      setIsVisible(false);
      return;
    }

    if (!isUserLoading) {
      if (isUserLoggedIn) {
        localStorage.setItem('cookieWarningShown', 'true');
        setIsVisible(false);
      } else {
        setIsVisible(!localStorage.getItem('cookieWarningDismissed'));
      }
    }
  }, [hasUserInteracted, userStore?.isUserLoggedIn, userStore?.isUserLoading]);

  const handleAccept = (): void => {
    localStorage.setItem('cookieWarningShown', 'true');
    setIsVisible(false);
    initMetrica(true);
  };

  const handleClose = (): void => {
    localStorage.setItem('cookieWarningDismissed', 'true');
    setIsVisible(false);
  };

  useEffect(() => {
    setCookieWarningVisible(isVisible);
    return () => {
      setCookieWarningVisible(false);
    };
  }, [isVisible]);

  useEffect(() => {
    const cookieWarningShown = localStorage.getItem('cookieWarningShown');

    // forceMetrika приходит из конфига уже после маунта: если флаг включён,
    // метрика переинициализируется с полным доступом.
    initMetrica(forceMetrika || Boolean(cookieWarningShown));
  }, [forceMetrika]);

  if (IsPrerender()) {
    return null;
  }

  if (!hasUserInteracted) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`CookieWarning ${
        isDesktop ? 'CookieWarning--desktop' : 'CookieWarning--mobile'
      }`}
    >
      <div className='CookieWarning__container'>
        <div className='CookieWarning__content'>
          <div className='CookieWarning__header'>
            <div className='CookieWarning__title'>Мы используем cookie</div>
            <div className='CookieWarning__close' onClick={handleClose}>
              <Picture icon={crossGreyDark} height='24' width='24' />
            </div>
          </div>
          <div className='CookieWarning__text'>
            Чтобы сделать сайт удобнее для вас. Оставаясь на сайте, вы
            соглашаетесь на{' '}
            <Link to='/confidential' className='CookieWarning__link'>
              использование файлов cookie
            </Link>{' '}
            и{' '}
            <Link to='/pdn' className='CookieWarning__link'>
              {' '}
              обработку ваших персональных данных
            </Link>
            .
          </div>
          <Button
            fullWidth={true}
            size='medium'
            colour='red'
            rank='primary'
            text='Согласен'
            onClick={handleAccept}
          />
        </div>
      </div>
    </div>
  );
});

export default CookieWarning;
